'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PLATFORM_BRAND } from '../../utils/platformBrand';
import { client } from '../../utils/trpc';
import AppCard from './AppCard';
import { useOAuthReturn } from './useOAuthReturn';
type Props = {
  autoEnabled: boolean;
  issue?: boolean;
  onToggleAuto: () => void;
};
type FacebookPage = {
  id: string;
  name: string;
};
const FacebookCard = t0 => {
  const $ = _c(23);
  const {
    autoEnabled,
    issue,
    onToggleAuto
  } = t0;
  const queryClient = useQueryClient();
  const [pickerPages, setPickerPages] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["facebookHasToken"],
      queryFn: _temp,
      retry: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: status,
    isLoading
  } = useQuery(t1);
  let t2;
  if ($[1] !== queryClient) {
    t2 = async page => {
      const [error] = await client.platforms.facebook.connect.mutate({
        pageId: page.id,
        pageName: page.name
      });
      if (error) {
        toast(error.message || "Failed to connect Facebook Page");
        return;
      }
      toast(`Facebook connected to ${page.name}`);
      await queryClient.invalidateQueries({
        queryKey: ["facebookHasToken"]
      });
      setPickerPages(null);
    };
    $[1] = queryClient;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const connectPage = t2;
  let t3;
  if ($[3] !== connectPage) {
    t3 = async (code, state) => {
      const [error_0, data] = await client.platforms.facebook.getPages.mutate({
        code,
        state
      });
      if (error_0) {
        toast(error_0.message || "Failed to connect Facebook");
        return;
      }
      const pages = data?.pages ?? [];
      if (pages.length === 0) {
        toast("No Facebook Pages found. If you have a Page, you may not have shared it with Factiii \u2014 see the steps below the Connect button.");
      } else {
        if (pages.length === 1) {
          await connectPage(pages[0]);
        } else {
          setPickerPages(pages);
        }
      }
    };
    $[3] = connectPage;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useOAuthReturn("facebook", "Facebook", t3);
  const connect = _temp2;
  let t4;
  if ($[5] !== queryClient) {
    t4 = async () => {
      const [error_2] = await client.platforms.facebook.disconnect.mutate();
      if (error_2) {
        toast(error_2.message || "Failed to disconnect Facebook");
        return;
      }
      toast("Facebook account disconnected.");
      await queryClient.invalidateQueries({
        queryKey: ["facebookHasToken"]
      });
    };
    $[5] = queryClient;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const disconnect = t4;
  const t5 = !!status?.hasToken;
  const t6 = status?.pageName ? `Connected to ${status.pageName}` : undefined;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p>Requires a Facebook Page. Create one for free at{" "}<a href="https://facebook.com/pages/create" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">facebook.com/pages/create</a>.</p>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <>{t7}<p className="mt-1">Have a Page but see "No Facebook Pages found"? Facebook remembers which Pages you shared the first time you connected, and won't ask again. Open{" "}<a href="https://facebook.com/settings?tab=business_tools" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Business Integrations</a>{" "}(facebook.com/settings?tab=business_tools), remove Factiii, then connect again and tick your Page when Facebook asks.</p></>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== autoEnabled || $[10] !== disconnect || $[11] !== isLoading || $[12] !== issue || $[13] !== onToggleAuto || $[14] !== t5 || $[15] !== t6) {
    t9 = <AppCard label="Facebook" brand={PLATFORM_BRAND.FACEBOOK} loading={isLoading} connected={t5} accountText={t6} supports="Cross-posts text, photos and videos to a Page" note={t8} autoEnabled={autoEnabled} issue={issue} onConnect={connect} onDisconnect={disconnect} onToggleAuto={onToggleAuto} />;
    $[9] = autoEnabled;
    $[10] = disconnect;
    $[11] = isLoading;
    $[12] = issue;
    $[13] = onToggleAuto;
    $[14] = t5;
    $[15] = t6;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== connectPage || $[18] !== pickerPages) {
    t10 = pickerPages && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"><div className="mx-4 w-full max-w-md rounded-xl bg-background-secondary p-6 shadow-lg"><h3 className="mb-2 text-lg font-semibold text-textPrimary">Select a Facebook Page</h3><p className="mb-4 text-sm text-textSecondary">Choose which Page to cross-post to:</p><div className="space-y-2">{pickerPages.map(page_0 => <button key={page_0.id} type="button" onClick={() => void connectPage(page_0)} className="w-full rounded-lg border border-ds-border-standard bg-background-primary px-4 py-3 text-left text-sm font-medium text-textPrimary transition-colors hover:bg-primary/10">{page_0.name}</button>)}</div><button type="button" onClick={() => setPickerPages(null)} className="mt-4 w-full rounded-lg px-4 py-2 text-sm text-textSecondary hover:text-textPrimary">Cancel</button></div></div>;
    $[17] = connectPage;
    $[18] = pickerPages;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
export default FacebookCard;
function _temp() {
  return unwrapFromResult(client.platforms.facebook.hasToken.query());
}
async function _temp2() {
  const [error_1, data_0] = await client.platforms.facebook.getAuthUrl.query();
  if (error_1 || !data_0?.url) {
    toast("Failed to start Facebook connection");
    return;
  }
  window.location.href = data_0.url;
}